import { Accordion } from '@/app/components/Accordion'
import { useCohortHelpers } from '@/app/hooks/useCohortHelpers'
import { ICohort } from '@/types/course'
import { Label, Radio, RadioGroup, RadioGroupProps } from '@headlessui/react'
import { useTranslations } from 'next-intl'
import pluralize from 'pluralize'
import { ComponentProps, forwardRef, useEffect, useState } from 'react'

export type CohortSelectorProps = Omit<
  RadioGroupProps<'div', string | null>,
  'onChange'
> & {
  cohorts: ICohort[]
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export const CohortSelector = forwardRef<HTMLDivElement, CohortSelectorProps>(
  ({ cohorts, disabled, ...props }, ref) => {
    const t = useTranslations('CoursePage.EnrolmentCard')
    const mainCohorts = cohorts.slice(0, 3)
    const additionalCohorts = cohorts.slice(3)

    const [accordionIsOpen, setAccordionIsOpen] = useState(false)
    const additionalCohortIsSelected = additionalCohorts.some(
      (cohort) => cohort._id === props.value
    )

    useEffect(() => {
      if (disabled || additionalCohortIsSelected) {
        setAccordionIsOpen(true)
      } else if (!disabled && !additionalCohortIsSelected) {
        setAccordionIsOpen(false)
      }
    }, [disabled, additionalCohortIsSelected])

    return (
      <RadioGroup
        {...props}
        ref={ref}
        className={`flex flex-col gap-2 ${props.className ?? ''}`}
        onChange={(value) => {
          props.onChange?.({
            target: {
              value,
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>)
        }}
      >
        <Label
          className={`text-lg font-semibold ${disabled ? 'text-mydra-medium-gray' : 'text-mydra-black'}`}
        >
          {t('select_a_cohort_to_enroll')}
        </Label>
        {mainCohorts.map((cohort) => (
          <CohortSelectorItem
            disabled={disabled}
            key={cohort._id}
            cohort={cohort}
          />
        ))}
        {additionalCohorts.length > 0 && (
          <Accordion
            buttonPosition="bottom"
            key={accordionIsOpen ? 'open' : 'closed'}
            disabled={disabled || additionalCohortIsSelected}
            className="gap-2"
            title={(isOpen) => (
              <div
                className={`mt-2 w-full text-center text-xs font-semibold text-gray-light`}
              >
                {t(isOpen ? 'view_less' : 'view_more', {
                  count: additionalCohorts.length,
                  count_plural: pluralize('cohort', additionalCohorts.length),
                })}
              </div>
            )}
            open={accordionIsOpen}
            icon={null}
          >
            <div className="flex flex-col gap-2">
              {additionalCohorts.map((cohort) => (
                <CohortSelectorItem
                  disabled={disabled}
                  key={cohort._id}
                  cohort={cohort}
                />
              ))}
            </div>
          </Accordion>
        )}
      </RadioGroup>
    )
  }
)

CohortSelector.displayName = 'CohortSelector'

type CohortSelectorItemProps = {
  cohort: ICohort
  disabled?: boolean
} & ComponentProps<'div'>

function CohortSelectorItem({
  cohort,
  disabled,
  ...props
}: CohortSelectorItemProps) {
  const { formatCohortDate } = useCohortHelpers()

  return (
    <Radio key={cohort._id} value={cohort._id} disabled={disabled}>
      {(radioContext) => (
        <div
          {...props}
          className={`flex cursor-pointer gap-2 rounded-xl border px-5 py-4 text-sm ${props.className ?? ''}`}
        >
          <span
            className={`text-sm font-semibold ${disabled ? 'text-mydra-medium-gray' : 'text-mydra-black'}`}
          >
            {formatCohortDate(cohort)}
          </span>
          <div
            className={`ml-auto flex gap-2 self-baseline overflow-hidden rounded-full leading-6 ${
              radioContext.checked && !disabled ? 'border-4' : 'border-2'
            } ${radioContext.checked ? 'p-[4px]' : 'p-[6px]'} ${
              disabled
                ? 'border-grey-background'
                : radioContext.checked
                  ? 'border-black'
                  : ''
            }`}
          />
        </div>
      )}
    </Radio>
  )
}
